/**
 * Client-side Runtime Configuration
 * 
 * This module provides access to environment variables injected at runtime by SSR.
 * Used by client-side services like Supabase and PostHog.
 */

// Runtime config type
export interface RuntimeConfig {
  SUPABASE_URL: string;
  SUPABASE_ANON_KEY: string;
  POSTHOG_HOST?: string;
  POSTHOG_KEY?: string;
}

// Extend Window interface globally
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

/**
 * Get runtime configuration injected by SSR
 * Returns empty config during SSR to prevent crashes
 */
export function getClientRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') {
    // During SSR, return empty config to prevent crashes
    // This is OK because the client-side code won't actually run during SSR
    return {
      SUPABASE_URL: '',
      SUPABASE_ANON_KEY: '',
      POSTHOG_HOST: undefined,
      POSTHOG_KEY: undefined,
    };
  }
  
  const config = window.__RUNTIME_CONFIG__;
  if (!config) {
    throw new Error('Runtime configuration not available. Make sure SSR is properly configured.');
  }
  
  return config;
}

/**
 * Check if we're running in development mode
 */
export function isDevelopment(): boolean {
  if (typeof window === 'undefined') {
    return process.env.NODE_ENV === 'development';
  }
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}